<template>
  <div class="tw:flex tw:flex-col">
    <div class="tw:mb-2 tw:flex tw:items-center tw:justify-between">
      <div class="tw:font-bold">
        <slot name="heading" />
      </div>
      <div>
        <a
          v-if="enabled"
          class="tw:cursor-pointer tw:text-denim-400 tw:hover:text-denim-700"
          data-testid="clear-all"
          @click="clearAll"
        >
          {{ getLocaleText("stream_filters_content.clear_all") }}
        </a>
      </div>
    </div>
    <div class="tw:flex tw:gap-4">
      <div class="tw:flex-1">
        <InputText
          v-model="localMin"
          :class="minClass"
          data-testid="min"
          new-design
          numbers-only
          :placeholder="localMinPlaceholder"
          type="number"
          @blur="validate('min')"
          @focus="minFocused = true"
        />
      </div>
      <div class="tw:flex tw:items-center">
        {{ getLocaleText("global.to") }}
      </div>
      <div class="tw:flex-1">
        <InputText
          v-model="localMax"
          :class="maxClass"
          data-testid="max"
          new-design
          numbers-only
          :placeholder="localMaxPlaceholder"
          type="number"
          @blur="validate('max')"
          @focus="maxFocused = true"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { InputText } from "shared/components/base";
import { clampBetweenValues } from "shared/helpers/number";
import { type StreamFiltersMinMax } from "shared/helpers/StreamFilters";
import { type Nullable } from "shared/types";

interface StreamFiltersMinMaxProps {
  clamp?: Nullable<[number, number]>;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}

const props = withDefaults(defineProps<StreamFiltersMinMaxProps>(), {
  clamp: null,
  minPlaceholder: "",
  maxPlaceholder: "",
});

const minMax = defineModel<StreamFiltersMinMax>({ required: true });

const enabled = defineModel<boolean>("enabled");

const minFocused = ref<boolean>(false);
const maxFocused = ref<boolean>(false);

const localMinxMax = ref<StreamFiltersMinMax>({
  min: enabled.value ? minMax.value.min : null,
  max: enabled.value ? minMax.value.max : null,
});

const localMin = computed<string>({
  get() {
    return localMinxMax.value.min === null
      ? ""
      : String(localMinxMax.value.min);
  },
  set(value) {
    localMinxMax.value.min = value ? Number(value) : null;
  },
});

const localMax = computed<string>({
  get() {
    return localMinxMax.value.max === null
      ? ""
      : String(localMinxMax.value.max);
  },
  set(value) {
    localMinxMax.value.max = value ? Number(value) : null;
  },
});

const localMinPlaceholder = computed<string>(() => {
  if (minFocused.value) return "";

  if (localMax.value && !localMin.value) {
    return "-";
  }

  if (props.minPlaceholder) return props.minPlaceholder;

  return getLocaleText("global.min");
});

const localMaxPlaceholder = computed<string>(() => {
  if (maxFocused.value) return "";

  if (localMin.value && !localMax.value) {
    return `${localMin.value}+`;
  }

  if (props.maxPlaceholder) return props.maxPlaceholder;

  return getLocaleText("global.max");
});

const minClass = computed<string>(() => {
  if (localMax.value && !localMin.value && !minFocused.value) return "pending";

  return "";
});

const maxClass = computed<string>(() => {
  if (localMin.value && !localMax.value && !maxFocused.value) return "pending";

  return "";
});

function enableFilter() {
  minMax.value = localMinxMax.value;

  if (localMinxMax.value.min !== null || localMinxMax.value.max !== null) {
    enabled.value = true;
  } else {
    enabled.value = false;
  }
}

function validate(metric: keyof StreamFiltersMinMax): void {
  let { min, max } = localMinxMax.value;

  if (metric === "min") {
    minFocused.value = false;

    if (min !== null) {
      if (props.clamp) {
        min = clampBetweenValues(min, ...props.clamp);
      }

      if (max !== null && min > max) {
        min = max;
      }

      if (!props.clamp && min < 0) {
        min = null;
      }
    }
  }

  if (metric === "max") {
    maxFocused.value = false;

    if (max !== null && max >= 0) {
      if (props.clamp) {
        max = clampBetweenValues(max, ...props.clamp);
      }

      if (min !== null && max < min) {
        max = min;
      }

      if (!props.clamp && max < 0) {
        max = null;
      }
    }
  }

  localMinxMax.value = { min, max };

  enableFilter();
}

function clearAll(): void {
  minMax.value = {
    min: null,
    max: null,
  };

  enabled.value = false;
}

watch(minMax, () => {
  localMinxMax.value = minMax.value;
});
</script>

<style lang="scss" scoped>
.pending {
  --s-input-default-bg: var(--s-color-denim-1);
}
</style>
